import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { IndonesiaMap } from "@/components/IndonesiaMap";
import { ChatLauncher } from "@/components/ChatLauncher";
import { useLang, LangToggle } from "@/lib/i18n";
import { ArrowRight, Download, ShieldCheck, Server, Network, Headset, Cpu, MapPin } from "lucide-react";

const SHOW_VIDEO = false;
const IMG = "https://grstore.id/assets/img/clients";
const CLIENT_LOGOS = [
  ["Pertamina EP", "kami/kami-1.jpeg"], ["PT PP London Sumatra", "kami/kami-3.jpeg"],
  ["PT CEVA Logistics", "kami/kami-4.jpeg"], ["PT Antam", "kami/kami-5.jpeg"],
  ["PT Timbang Deli Indonesia", "kami/kami-6.jpeg"], ["PT Tirta Investama (AQUA)", "aqua.png"],
  ["Mestindo", "mest.jpeg"], ["Kopi Kenangan", "mestindo.jpeg"],
].map(([name, file]) => ({ name, src: `${IMG}/${file}` }));
const PARTNER_LOGOS = [
  ["Defend IT360", "mitra/mitra-1.jpeg"], ["PT Mitra Integrasi Informatika", "mitra/mitra-2.jpeg"],
  ["Microsoft", "mitra/microsoft.jpeg"], ["SIS", "mitra/sis.jpeg"],
].map(([name, file]) => ({ name, src: `${IMG}/${file}` }));

function LogoMarquee({ id, logos, reverse }) {
  const set = Array.from({ length: Math.ceil(8 / logos.length) }, () => logos).flat();
  return (
    <div className="overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]" data-testid={`landing-${id}-marquee`}>
      <div className={`logo-marquee ${reverse ? "logo-marquee-reverse" : ""} flex w-max gap-4`}>
        {[...set, ...set].map((l, i) => (
          <div key={`${l.src}-${i}`} className="bg-white border border-border rounded-sm h-28 w-56 shrink-0 flex items-center justify-center p-4" data-testid={`landing-${id}-logo`} aria-hidden={i >= logos.length}>
            <img src={l.src} alt={l.name} title={l.name} loading="lazy" className="max-h-full max-w-full object-contain" />
          </div>
        ))}
      </div>
    </div>
  );
}

function LogoStrip({ id, title, overline, logos, className = "", marquee = false, reverse = false }) {
  return (
    <section id={id} className={`py-16 ${className}`} data-testid={`landing-${id}-section`}>
      <div className="max-w-6xl mx-auto px-6">
        <div className="overline text-accent mb-2">{overline}</div>
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight mb-10" data-testid={`landing-${id}-title`}>{title}</h2>
        {marquee ? <LogoMarquee id={id} logos={logos} reverse={reverse} /> : <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {logos.map((l) => (
            <div key={l.src} className="bg-white border border-border rounded-sm h-28 flex items-center justify-center p-4 hover:border-primary/40 transition-colors" data-testid={`landing-${id}-logo`}>
              <img src={l.src} alt={l.name} title={l.name} loading="lazy" className="max-h-full max-w-full object-contain" />
            </div>
          ))}
        </div>}
      </div>
    </section>
  );
}

const SERVICES = [
  { icon: Server, title: "Server & Data Center", desc: "Pengadaan, instalasi, dan pemeliharaan infrastruktur server perusahaan." },
  { icon: Network, title: "Networking & Security", desc: "Perangkat jaringan, firewall, dan solusi keamanan siber terintegrasi." },
  { icon: Cpu, title: "Perangkat IT & Lisensi", desc: "Laptop, workstation, dan lisensi software resmi dengan harga kompetitif." },
  { icon: Headset, title: "Managed IT Support", desc: "Dukungan teknis on-site & remote untuk operasional yang selalu berjalan." },
];

export default function Landing() {
  const [company, setCompany] = useState({ name: "CV GRCom", tagline: "IT Support & Infrastruktur", phone: "", email: "", website: "" });
  useEffect(() => { api.get("/public/company").then((r) => setCompany(r.data)).catch(() => {}); }, []);
  const { t, lang } = useLang();

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Nav */}
      <header className="sticky top-0 z-30 bg-background/90 backdrop-blur border-b border-border">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/grcom-logo.png" alt={company.name} className="h-11 sm:h-12 w-auto" data-testid="landing-logo" />
          </div>
          <div className="flex items-center gap-3">
            <a href="#layanan" className="text-sm text-muted-foreground hover:text-foreground hidden sm:block">{t("Layanan")}</a>
            <a href="#jangkauan" className="text-sm text-muted-foreground hover:text-foreground hidden sm:block">{t("Jangkauan")}</a>
            <a href={`/company-profile/GRCom-Company-Profile${lang === "en" ? "-EN" : ""}.pdf`} download data-testid="landing-nav-profile-link" className="text-sm text-muted-foreground hover:text-foreground hidden sm:inline-flex items-center gap-1"><Download className="w-3.5 h-3.5" /> {t("Profil")}</a>
            <LangToggle />
            <Link to="/request" data-testid="landing-cta-nav" className="text-sm font-medium bg-primary text-primary-foreground px-4 py-2 rounded-sm hover:opacity-90 transition-opacity">
              {t("Penawaran")}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-[#EEF3F8] border-b border-border" data-testid="landing-hero">
      <div className="max-w-6xl mx-auto px-6 pt-16 pb-12 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        <div className="fade-up">
          <div className="overline text-accent mb-4">{t(company.tagline)}</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-5" data-testid="landing-hero-title">
            {t("Perusahaan Infrastruktur IT untuk Bisnis Anda")}
          </h1>
          <p className="text-muted-foreground text-base max-w-lg mb-8">
            {company.name} {lang === "en" ? t("hero_desc") : "menyediakan pengadaan perangkat, jaringan, server, dan dukungan IT terpercaya bagi perusahaan di seluruh Indonesia."}
          </p>
          <div className="flex items-center gap-4">
            <Link to="/request" data-testid="landing-cta-hero" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-sm font-medium hover:opacity-90 transition-opacity">
              {t("Penawaran")} <ArrowRight className="w-4 h-4" />
            </Link>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <ShieldCheck className="w-4 h-4 text-accent" /> {t("Distributor & Principal Resmi")}
            </div>
          </div>
        </div>
        <div className="bg-white border border-border rounded-sm p-4 shadow-sm fade-up" id="jangkauan">
          <div className="flex items-center gap-2 mb-2 px-2">
            <span className="relative flex h-3 w-3"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent/50" /><MapPin className="w-3.5 h-3.5 text-accent" /></span>
            <span className="text-sm font-medium">{t("Kantor Pusat")}: <span className="text-accent font-semibold">Medan</span> · Area Support Team</span>
          </div>
          <IndonesiaMap />
        </div>
      </div>
      </section>

      {SHOW_VIDEO && <section className="max-w-6xl mx-auto px-6 py-16" data-testid="landing-video-section">
        <div className="overline text-accent mb-2">Cara Kerja Kami</div>
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight mb-3">Lihat Cara Kerja Kami</h2>
        <p className="text-muted-foreground mb-8 max-w-2xl">Dari permintaan produk, penawaran harga, hingga invoice &mdash; semuanya cepat dan transparan.</p>
        <div className="bg-white border border-border rounded-sm p-2 shadow-sm max-w-4xl">
          <video src="/video/grcom-intro.mp4" poster="/video/grcom-intro.jpg" controls preload="none" playsInline className="w-full aspect-video rounded-sm bg-black" data-testid="landing-intro-video" />
        </div>
      </section>}

      {/* Services */}
      <section id="layanan" className="bg-secondary/40 border-y border-border py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="overline text-accent mb-2">{t("Layanan Kami")}</div>
          <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight mb-10">{t("Solusi IT Menyeluruh")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICES.map((s, i) => (
              <div key={i} className="bg-card border border-border rounded-sm p-6 hover:border-primary/40 transition-colors" data-testid={`service-${i}`}>
                <s.icon className="w-7 h-7 text-primary mb-4" />
                <h3 className="font-heading font-bold mb-2">{t(s.title)}</h3>
                <p className="text-sm text-muted-foreground">{t(s.desc)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <LogoStrip id="klien" title={t("Klien Kami")} overline={t("Dipercaya Perusahaan Nasional")} logos={CLIENT_LOGOS} marquee />
      <LogoStrip id="mitra" title={t("Mitra Kami")} overline={t("Partner Teknologi")} logos={PARTNER_LOGOS} marquee reverse className="bg-[#F7F3EC] border-y border-[#EADFCF]" />

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-6 py-20 text-center">
        <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight mb-4">{t("Butuh penawaran cepat?")}</h2>
        <p className="text-muted-foreground mb-8 max-w-xl mx-auto">{t("Sampaikan kebutuhan Anda. Tim AI Admin kami menyusun penawaran harga terbaik dan menghubungi Anda.")}</p>
        <Link to="/request" data-testid="landing-cta-bottom" className="inline-flex items-center gap-2 bg-accent text-accent-foreground px-8 py-3.5 rounded-sm font-medium hover:opacity-90 transition-opacity">
          {t("Ajukan Permintaan")} <ArrowRight className="w-4 h-4" />
        </Link>
        <div className="mt-10 border-t border-border pt-8" data-testid="landing-profile-download">
          <div className="text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground mb-4">Company Profile</div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a href="/company-profile/GRCom-Company-Profile.pdf" download data-testid="download-profile-id-btn"
              className="inline-flex items-center gap-2 border border-primary text-primary px-6 py-3 rounded-sm font-medium hover:bg-primary hover:text-primary-foreground transition-colors">
              <Download className="w-4 h-4" /> Unduh (Bahasa Indonesia)
            </a>
            <a href="/company-profile/GRCom-Company-Profile-EN.pdf" download data-testid="download-profile-en-btn"
              className="inline-flex items-center gap-2 border border-primary text-primary px-6 py-3 rounded-sm font-medium hover:bg-primary hover:text-primary-foreground transition-colors">
              <Download className="w-4 h-4" /> Download (English)
            </a>
          </div>
        </div>
      </section>

      <footer className="border-t border-border py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-muted-foreground">
          <div className="flex flex-wrap items-center gap-4">
            {company.phone && <span>{company.phone}</span>}
            {company.email && <span>{company.email}</span>}
            {company.website && <a href={company.website} className="text-primary hover:underline">{company.website}</a>}
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6 mt-4">
          <Link to="/login" data-testid="landing-admin-login-link" className="text-[9px] text-muted-foreground/15 hover:text-muted-foreground/60 transition-colors">
            Portal
          </Link>
        </div>
      </footer>
      <ChatLauncher />
    </div>
  );
}
